import React from "react";
import type { Box } from "./frame";
import type { Theme } from "./theme";

/**
 * Anivaze v4 — RHYTHM / AUDIO-VIZ actor (PLATFORM-ADDITIONS §1).
 *
 * Fixes the stakeholder defect: a music/rhythm topic rendered the SAME instrument
 * image again and again instead of VISUALIZING the phenomenon. This draws the
 * phenomenon itself, as a pure function of `t`, looping to the beat:
 *
 *   pulse     — an equalizer that pumps on the beat + a 4-beat counter with a
 *               sweeping playhead and an accented downbeat. "rhythm / beat / groove".
 *   wave      — a live waveform (fundamental + harmonics) scrolling and breathing.
 *               "sound wave / frequency / timbre / overtone".
 *   staff     — a 5-line staff with a melody appearing under a playhead. "notation".
 *   metronome — a swinging pendulum ticking at the tempo. "tempo / bpm / keep time".
 */

const TAU = Math.PI * 2;
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (x: number) => { const c = clamp01(x); return c * c * (3 - 2 * c); };
const rnd = (i: number, s = 1) => { const x = Math.sin((i + 1) * 12.9898 * s + 78.233) * 43758.5453; return x - Math.floor(x); };

export type RhythmSubject = "pulse" | "wave" | "staff" | "metronome";
export interface RhythmConfig { subject: RhythmSubject; bpm: number }

export function parseRhythmConfig(text: string): RhythmConfig {
  const s = (text ?? "").toLowerCase();
  const m = s.match(/(\d{2,3})\s*bpm/);
  const bpm = m ? Math.max(40, Math.min(220, parseInt(m[1], 10))) : 96;
  const subject: RhythmSubject =
      /metronome|keep(s|ing)? time|\btempo\b|\bbpm\b|steady (beat|pulse)|click track/.test(s) ? "metronome"
    : /wave ?form|sound ?wave|oscillat|\bfrequency\b|\bamplitude\b|vibrat|timbre|overtone|\bharmonic|sine wave|\bpitch\b/.test(s) ? "wave"
    : /\bstaff\b|notation|sheet music|\bscore\b|\bmelody\b|\bnotes?\b|\bchords?\b|\bscale\b|octave|\bclef\b/.test(s) ? "staff"
    : "pulse"; // rhythm | beat | groove | syncopation | meter | time signature
  return { subject, bpm };
}

/** Route music/audio-mechanism beats to the rhythm viz — NOT to a repeated instrument image. */
export const RHYTHM_RE =
  /\brhythm\b|\bbeats?\b|\btempo\b|\bbpm\b|metronome|\bgroove\b|syncopat|time signature|down ?beat|wave ?form|sound ?wave|oscillat|\bfrequency\b|\bamplitude\b|timbre|overtone|\bharmonics?\b|\bmelody\b|\bnotation\b|sheet music|\bpitch\b/i;

interface VProps { items: string[]; box: Box; theme: Theme; unit: number; t: number }

function caption(text: string, box: Box, theme: Theme, u: number) {
  return (
    <text x={box.cx} y={box.y + box.h - 1.5 * u} textAnchor="middle"
      fontFamily={theme.body.family} fontWeight={600} fontSize={2.1 * u}
      fill={theme.palette.sub} opacity={0.85}>{text}</text>
  );
}

// ── pulse: equalizer + 4-beat counter ───────────────────────────────────────
function Pulse({ box, theme, unit: u, t, bpm }: VProps & { bpm: number }) {
  const P = theme.palette;
  const beatDur = 60 / bpm;
  const bars = 11;
  const gap = 1.2 * u;
  const areaW = box.w * 0.78;
  const barW = (areaW - gap * (bars - 1)) / bars;
  const x0 = box.cx - areaW / 2;
  const baseY = box.cy + box.h * 0.12;
  const maxH = box.h * 0.42;
  const spike = Math.pow(Math.max(0, 1 - (t % beatDur) / beatDur), 1.6);
  const beatIdx = Math.floor(t / beatDur);
  const rects = Array.from({ length: bars }, (_, i) => {
    const osc = 0.5 + 0.5 * Math.sin(TAU * t * (0.8 + 0.14 * i) / beatDur + i);
    const v = clamp01(0.22 + 0.5 * osc + 0.55 * spike * rnd(i, beatIdx + 1));
    const h = 0.12 * maxH + v * maxH;
    const x = x0 + i * (barW + gap);
    return <rect key={i} x={x} y={baseY - h} width={barW} height={h} rx={barW * 0.35}
      fill={P.primary} opacity={0.55 + 0.45 * v} />;
  });
  // 4-beat counter row with playhead
  const bpb = 4;
  const barPhase = (t % (beatDur * bpb)) / (beatDur * bpb);
  const active = Math.floor(barPhase * bpb) % bpb;
  const dotY = box.y + box.h * 0.16;
  const dotSpan = box.w * 0.5;
  const dx0 = box.cx - dotSpan / 2;
  const dots = Array.from({ length: bpb }, (_, i) => {
    const on = i === active;
    const down = i === 0;
    const x = dx0 + (dotSpan / (bpb - 1)) * i;
    const r = (down ? 1.5 : 1.2) * u * (on ? 1.35 : 1);
    return <circle key={i} cx={x} cy={dotY} r={r}
      fill={down ? P.highlight : P.primary} opacity={on ? 1 : 0.32} />;
  });
  const headX = dx0 + dotSpan * (active / (bpb - 1));
  return (
    <g>
      {dots}
      <line x1={headX} y1={dotY - 3 * u} x2={headX} y2={dotY + 3 * u} stroke={P.highlight} strokeWidth={theme.strokeWidth * 0.6} opacity={0.5} />
      <line x1={x0 - u} y1={baseY} x2={x0 + areaW + u} y2={baseY} stroke={P.muted} strokeWidth={theme.strokeWidth * 0.5} opacity={0.4} />
      {rects}
      {caption("4/4 · " + bpm + " bpm", box, theme, u)}
    </g>
  );
}

// ── wave: fundamental + harmonics ────────────────────────────────────────────
function Wave({ box, theme, t }: VProps) {
  const P = theme.palette;
  const cy = box.cy;
  const w = box.w * 0.86;
  const x0 = box.cx - w / 2;
  const amp = box.h * 0.24 * (0.7 + 0.3 * Math.sin(t * 1.3));
  const N = 140;
  const pts: string[] = [];
  for (let i = 0; i <= N; i++) {
    const fx = i / N;
    const x = x0 + fx * w;
    const ph = TAU * fx * 2 - t * 3.2;
    const y = cy - amp * (Math.sin(ph) + 0.5 * Math.sin(2 * ph + 0.6) + 0.28 * Math.sin(3 * ph + 1.2)) / 1.78;
    pts.push(`${x.toFixed(1)} ${y.toFixed(1)}`);
  }
  return (
    <g>
      <line x1={x0} y1={cy} x2={x0 + w} y2={cy} stroke={P.muted} strokeWidth={theme.strokeWidth * 0.5} opacity={0.35} />
      <polyline points={pts.join(" ")} fill="none" stroke={P.primary}
        strokeWidth={theme.strokeWidth * 1.1} strokeLinecap="round" strokeLinejoin="round" />
      {caption("sound wave · fundamental + harmonics", box, theme, box.h / 34)}
    </g>
  );
}

// ── staff: notation with a playhead ──────────────────────────────────────────
function Staff({ box, theme, unit: u, t }: VProps) {
  const P = theme.palette;
  const lines = 5;
  const stepY = 1.6 * u;
  const topY = box.cy - (lines - 1) * stepY / 2;
  const w = box.w * 0.84;
  const x0 = box.cx - w / 2;
  const staffLines = Array.from({ length: lines }, (_, i) => (
    <line key={i} x1={x0} y1={topY + i * stepY} x2={x0 + w} y2={topY + i * stepY}
      stroke={P.sub} strokeWidth={theme.strokeWidth * 0.4} opacity={0.5} />
  ));
  const melody = [2, 1, 3, 0, 2, 4, 1, 3]; // scale-degree-ish positions
  const nN = melody.length;
  const cycle = 4.4;
  const phase = (t % cycle) / cycle;
  const headX = x0 + phase * w;
  const notes = melody.map((deg, i) => {
    const nx = x0 + (w / (nN + 0.5)) * (i + 0.7);
    const ny = topY + (lines - 1) * stepY - deg * stepY * 0.5;
    const appear = smooth(clamp01((headX - nx) / (2 * u) + 0.5)) * (nx <= headX + 1.5 * u ? 1 : 0.12);
    return (
      <g key={i} opacity={0.15 + 0.85 * appear}>
        <ellipse cx={nx} cy={ny} rx={1.05 * u} ry={0.8 * u} fill={P.primary} transform={`rotate(-18 ${nx} ${ny})`} />
        <line x1={nx + 1.0 * u} y1={ny} x2={nx + 1.0 * u} y2={ny - 4.2 * u} stroke={P.primary} strokeWidth={theme.strokeWidth * 0.5} />
      </g>
    );
  });
  return (
    <g>
      {staffLines}
      {notes}
      <line x1={headX} y1={topY - 2 * u} x2={headX} y2={topY + (lines - 1) * stepY + 2 * u}
        stroke={P.highlight} strokeWidth={theme.strokeWidth * 0.6} opacity={0.6} />
      {caption("notation · melody", box, theme, u)}
    </g>
  );
}

// ── metronome: swinging pendulum ─────────────────────────────────────────────
function Metronome({ box, theme, unit: u, t, bpm }: VProps & { bpm: number }) {
  const P = theme.palette;
  const beatDur = 60 / bpm;
  const pivotX = box.cx;
  const pivotY = box.cy - box.h * 0.18;
  const armLen = box.h * 0.42;
  const maxA = 0.5; // radians
  const ang = maxA * Math.sin(TAU * (t / (2 * beatDur)));
  const bobT = 0.62;
  const ex = pivotX + Math.sin(ang) * armLen;
  const ey = pivotY + Math.cos(ang) * armLen;
  const bx = pivotX + Math.sin(ang) * armLen * bobT;
  const by = pivotY + Math.cos(ang) * armLen * bobT;
  const bw = box.w * 0.34;
  return (
    <g>
      <path d={`M ${pivotX - bw / 2} ${pivotY + armLen} L ${pivotX - bw * 0.18} ${pivotY} L ${pivotX + bw * 0.18} ${pivotY} L ${pivotX + bw / 2} ${pivotY + armLen} Z`}
        fill={P.surface2} stroke={P.primary} strokeWidth={theme.strokeWidth * 0.8} strokeLinejoin="round" />
      <line x1={pivotX} y1={pivotY} x2={ex} y2={ey} stroke={P.ink} strokeWidth={theme.strokeWidth} strokeLinecap="round" />
      <rect x={bx - 1.4 * u} y={by - 1.0 * u} width={2.8 * u} height={2.0 * u} rx={0.4 * u} fill={P.highlight} />
      <circle cx={pivotX} cy={pivotY} r={0.9 * u} fill={P.primary} />
      {caption(bpm + " bpm", box, theme, u)}
    </g>
  );
}

const SUBJECTS: RhythmSubject[] = ["pulse", "wave", "staff", "metronome"];

export function RhythmActor(props: VProps) {
  const raw = (props.items ?? []).join(" ");
  const direct = (props.items?.[0] ?? "").trim().toLowerCase() as RhythmSubject;
  // The director resolves the subject once and passes it as items[0]; fall back to
  // parsing free text (deterministic / manual-script path).
  const cfg: RhythmConfig = SUBJECTS.includes(direct)
    ? { subject: direct, bpm: parseRhythmConfig(raw).bpm }
    : parseRhythmConfig(raw);
  switch (cfg.subject) {
    case "wave": return <Wave {...props} />;
    case "staff": return <Staff {...props} />;
    case "metronome": return <Metronome {...props} bpm={cfg.bpm} />;
    default: return <Pulse {...props} bpm={cfg.bpm} />;
  }
}
